import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { EMPLOYMENT_AGREEMENT_SYSTEM_PROMPT_JSON } from '@/lib/openai';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileCheck,
  FilePlus,
  FileSearch,
  FileText,
  Files,
  Folder,
  Handshake,
  Link2,
  Loader2,
  Lock,
  RotateCcw,
  Scale,
  Shield,
  Sparkles,
  UserCheck,
  Users,
  Wand2,
  X,
} from 'lucide-react';

import { slugify } from '@/lib/utils';
import { TEMPLATE_CATEGORIES } from '@/modules/templates/constants/template-categories';
import { useCreateTemplate } from '@/modules/templates/hooks/use-templates';
import { checkSlugAvailability } from '@/modules/templates/services/templates.service';
import { syncTemplateSave } from '../lib/sync-template-uilm';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';

// Default prompts by template slug
const defaultPrompts: Record<string, string> = {
  'employment-agreement': EMPLOYMENT_AGREEMENT_SYSTEM_PROMPT_JSON,
};

const createFormSchema = (t: any) =>
  z.object({
    // Slug ≤ 50 chars guarantees every generated UILM key fits within
    // the 100-char limit (longest suffix = `_SCR_S{8HEX}_FLD_F{8HEX}_PLACEHOLDER`
    // = 40 chars). See `clampSlugForKey` for the defensive backstop.
    slug: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_SLUGREQUIRED'))
      .max(50, t('ORGTEMPLATES_VALIDATION_SLUGMAX'))
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t('ORGTEMPLATES_VALIDATION_SLUGFORMAT')),
    title: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_TITLEREQUIRED'))
      .max(200, t('ORGTEMPLATES_VALIDATION_TITLEMAX')),
    description: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_DESCRIPTIONREQUIRED'))
      .max(1000, t('ORGTEMPLATES_VALIDATION_DESCRIPTIONMAX')),
    icon: z.string().default('FileText'),
    category: z.string().optional().nullable(),
    available: z.boolean().default(false),
    popular: z.boolean().default(false),
    // href is auto-generated from slug
    keywords: z.array(z.string()).default([]).optional(),
    estimatedMinutes: z.number().int().positive().optional().nullable(),
    systemPrompt: z.string().optional().nullable(),
  });

type FormInput = z.input<ReturnType<typeof createFormSchema>>;

export default function NewTemplatePage() {
  const router = useNavigate();

  const { t } = useTranslation();
  const { toast } = useToast();

  const formSchema = createFormSchema(t);

  // Icon options
  const iconOptions = [
    {
      value: 'FileText',
      label: t('ORGTEMPLATES_ICONS_FILETEXT'),
      icon: FileText,
    },
    { value: 'Users', label: t('ORGTEMPLATES_ICONS_USERS'), icon: Users },
    { value: 'Lock', label: t('ORGTEMPLATES_ICONS_LOCK'), icon: Lock },
    { value: 'Shield', label: t('ORGTEMPLATES_ICONS_SHIELD'), icon: Shield },
    {
      value: 'Sparkles',
      label: t('ORGTEMPLATES_ICONS_SPARKLES'),
      icon: Sparkles,
    },
    {
      value: 'FileCheck',
      label: t('ORGTEMPLATES_ICONS_FILECHECK'),
      icon: FileCheck,
    },
    {
      value: 'FilePlus',
      label: t('ORGTEMPLATES_ICONS_FILEPLUS'),
      icon: FilePlus,
    },
    {
      value: 'FileSearch',
      label: t('ORGTEMPLATES_ICONS_FILESEARCH'),
      icon: FileSearch,
    },
    { value: 'Files', label: t('ORGTEMPLATES_ICONS_FILES'), icon: Files },
    { value: 'Folder', label: t('ORGTEMPLATES_ICONS_FOLDER'), icon: Folder },
    { value: 'Scale', label: t('ORGTEMPLATES_ICONS_SCALE'), icon: Scale },
    {
      value: 'Briefcase',
      label: t('ORGTEMPLATES_ICONS_BRIEFCASE'),
      icon: Briefcase,
    },
    {
      value: 'Building',
      label: t('ORGTEMPLATES_ICONS_BUILDING'),
      icon: Building,
    },
    {
      value: 'Handshake',
      label: t('ORGTEMPLATES_ICONS_HANDSHAKE'),
      icon: Handshake,
    },
    {
      value: 'UserCheck',
      label: t('ORGTEMPLATES_ICONS_USERCHECK'),
      icon: UserCheck,
    },
    {
      value: 'ClipboardList',
      label: t('ORGTEMPLATES_ICONS_CLIPBOARDLIST'),
      icon: ClipboardList,
    },
  ] as const;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [aiConfigExpanded, setAiConfigExpanded] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const slugCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slug: '',
      title: '',
      description: '',
      icon: 'FileText',
      category: null,
      available: false,
      popular: false,
      // href will be auto-generated
      keywords: [],
      estimatedMinutes: null,
      systemPrompt: null,
    },
  });

  const { mutateAsync: createTemplateMutation } = useCreateTemplate();

  // Realtime slug availability (mirrors the org template create flow).
  const checkSlug = useCallback(async (slug: string) => {
    if (!slug || slug.length < 2) {
      setSlugAvailable(null);
      return;
    }
    setIsCheckingSlug(true);
    try {
      setSlugAvailable(await checkSlugAvailability(slug));
    } catch {
      setSlugAvailable(null);
    } finally {
      setIsCheckingSlug(false);
    }
  }, []);

  const debouncedSlugCheck = useCallback(
    (slug: string) => {
      if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
      slugCheckTimeout.current = setTimeout(() => checkSlug(slug), 300);
    },
    [checkSlug]
  );

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    form.setValue('title', title);
    if (!slugManuallyEdited) {
      const newSlug = slugify(title);
      form.setValue('slug', newSlug);
      debouncedSlugCheck(newSlug);
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    form.setValue('slug', slug);
    setSlugManuallyEdited(true);
    debouncedSlugCheck(slug);
  };

  const handleResetSlug = () => {
    const newSlug = slugify(form.getValues('title'));
    form.setValue('slug', newSlug);
    setSlugManuallyEdited(false);
    debouncedSlugCheck(newSlug);
  };

  const handleSubmit = async (data: FormInput) => {
    setIsSubmitting(true);
    try {
      const parsedData = formSchema.parse(data);
      const createResult = await createTemplateMutation({
        Slug: parsedData.slug,
        Title: parsedData.title,
        Description: parsedData.description,
        Icon: parsedData.icon,
        Available: parsedData.available,
        Popular: parsedData.popular,
        Href: `/templates/${parsedData.slug}/generate`,
        Category: parsedData.category ?? undefined,
        EstimatedMinutes: parsedData.estimatedMinutes ?? undefined,
        SystemPrompt: parsedData.systemPrompt ?? undefined,
        Keywords: parsedData.keywords,
      });

      const newSlug = parsedData.slug;
      toast({
        variant: 'success',
        title: 'Success',
        description: t('ORGTEMPLATES_NEW_TOAST_CREATESUCCESS'),
      });

      // Push template-level translation keys to UILM. Non-fatal: if it
      // fails the template is still saved in UDS and the admin can retry
      // from the Translations tab on the edit page.
      const newTemplateId = (createResult as { insertTemplates?: { itemId?: string } })
        ?.insertTemplates?.itemId;
      if (newTemplateId) {
        try {
          await syncTemplateSave({
            templateId: newTemplateId,
            slug: parsedData.slug,
            title: parsedData.title,
            description: parsedData.description,
          });
        } catch (uilmErr) {
          console.warn('UILM sync deferred for new template:', uilmErr);
          toast({
            variant: 'default',
            title: 'Translation sync deferred',
            description:
              'Template was created, but its translation keys could not be pushed to UILM. Open the Translations tab on the edit page to retry.',
          });
        }
      }

      if (newSlug) {
        router(`/admin/templates/${newSlug}/edit`);
      } else {
        router('/admin/templates');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          error instanceof Error ? error.message : t('ORGTEMPLATES_NEW_TOAST_CREATEERROR'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addKeyword = () => {
    const keyword = keywordInput.trim().toLowerCase();
    const currentKeywords = form.getValues('keywords') ?? [];
    if (keyword && !currentKeywords.includes(keyword)) {
      form.setValue('keywords', [...currentKeywords, keyword]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    const currentKeywords = form.getValues('keywords') ?? [];
    form.setValue(
      'keywords',
      currentKeywords.filter((k) => k !== keyword)
    );
  };

  // Slug is handled by form.register

  const selectedIcon =
    iconOptions.find((opt) => opt.value === form.watch('icon'))?.icon || FileText;
  const IconComponent = selectedIcon;

  return (
    <div className="container md:py-8 px-2 md:px-4 pb-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4 md:mb-8">
        <Link to={`/admin/templates`}>
          <Button variant="ghost" size="icon" className="bg-brand-surface">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-selise-blue/10 flex items-center justify-center">
            <FilePlus className="h-6 w-6 text-selise-blue" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[hsl(var(--fg))]">
              {t('ORGTEMPLATES_NEW_HEADER_TITLE')}
            </h1>
            <p className="text-sm text-globe-grey">{t('ORGTEMPLATES_NEW_HEADER_SUBTITLE')}</p>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('ORGTEMPLATES_FORM_TITLE')}</CardTitle>
            <CardDescription>{t('ORGTEMPLATES_FORM_DESCRIPTION')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title and Slug */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">{t('ORGTEMPLATES_FORM_TITLELABEL')}</Label>
                <Input
                  id="title"
                  placeholder={t('ORGTEMPLATES_FORM_TITLEPLACEHOLDER')}
                  value={form.watch('title')}
                  onChange={handleTitleChange}
                />
                {form.formState.errors.title && (
                  <p className="text-sm text-destructive">{form.formState.errors.title.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="slug">{t('ORGTEMPLATES_FORM_SLUGLABEL')}</Label>
                  {slugManuallyEdited && (
                    <button
                      type="button"
                      onClick={handleResetSlug}
                      className="flex items-center gap-1 text-xs text-selise-blue hover:underline"
                    >
                      <Link2 className="size-3" />
                      {t('ORGTEMPLATES_FORM_SLUGAUTOGENERATE')}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="slug"
                    placeholder={t('ORGTEMPLATES_FORM_SLUGPLACEHOLDER')}
                    value={form.watch('slug')}
                    onChange={handleSlugChange}
                    className={
                      slugAvailable === false
                        ? 'border-destructive pr-10'
                        : slugAvailable === true
                          ? 'border-lime-green pr-10'
                          : 'pr-10'
                    }
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isCheckingSlug ? (
                      <Loader2 className="size-4 animate-spin text-globe-grey" />
                    ) : slugAvailable === true ? (
                      <Check className="size-4 text-lime-green" />
                    ) : slugAvailable === false ? (
                      <AlertCircle className="size-4 text-destructive" />
                    ) : null}
                  </div>
                </div>
                {form.formState.errors.slug && (
                  <p className="text-sm text-destructive">{form.formState.errors.slug.message}</p>
                )}
                {slugAvailable === false && (
                  <p className="text-sm text-destructive">{t('ORGTEMPLATES_FORM_SLUGTAKEN')}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('ORGTEMPLATES_FORM_DESCRIPTIONLABEL')}</Label>
              <textarea
                id="description"
                placeholder={t('ORGTEMPLATES_FORM_DESCRIPTIONPLACEHOLDER')}
                className="flex min-h-24 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                {...form.register('description')}
              />
              {form.formState.errors.description && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            {/* Icon */}
            <div className="space-y-2">
              <Label>{t('ORGTEMPLATES_FORM_ICONLABEL')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-muted">
                  <IconComponent className="size-5 text-selise-blue" />
                </div>
                <select
                  className="flex h-10 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.watch('icon')}
                  onChange={(e) => form.setValue('icon', e.target.value)}
                >
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              <select
                className="flex h-10 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.watch('category') ?? ''}
                onChange={(e) => form.setValue('category', e.target.value || null)}
              >
                <option value="">Select a category</option>
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* URL Preview */}
            {form.watch('slug') && (
              <div className="space-y-2">
                <Label>{t('ORGTEMPLATES_FORM_URLPREVIEWLABEL')}</Label>
                <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-muted">
                  <code className="text-sm text-globe-grey">
                    /templates/{form.watch('slug')}/generate
                  </code>
                </div>
                <p className="text-xs text-globe-grey">{t('ORGTEMPLATES_FORM_URLPREVIEWHELP')}</p>
              </div>
            )}

            {/* Toggles */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  checked={form.watch('available')}
                  onChange={(e) => form.setValue('available', e.target.checked)}
                />
                <span className="text-sm font-medium text-[hsl(var(--fg))]">
                  {t('ORGTEMPLATES_FORM_AVAILABLELABEL')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  checked={form.watch('popular')}
                  onChange={(e) => form.setValue('popular', e.target.checked)}
                />
                <span className="text-sm font-medium text-[hsl(var(--fg))]">
                  {t('ORGTEMPLATES_FORM_POPULARLABEL')}
                </span>
              </label>
            </div>

            {/* Keywords */}
            <div className="space-y-2">
              <Label>{t('ORGTEMPLATES_FORM_KEYWORDSLABEL')}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder={t('ORGTEMPLATES_FORM_KEYWORDSPLACEHOLDER')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addKeyword}>
                  {t('ORGTEMPLATES_FORM_KEYWORDSADD')}
                </Button>
              </div>
              {(form.watch('keywords') ?? []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(form.watch('keywords') || []).map((keyword) => (
                    <Badge key={keyword} variant="secondary" className="gap-1 pr-1">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(keyword)}
                        className="ml-1 rounded-full hover:bg-destructive/20"
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Estimated Minutes */}
            <div className="space-y-2">
              <Label htmlFor="estimatedMinutes">
                {t('ORGTEMPLATES_FORM_ESTIMATEDMINUTESLABEL')}
              </Label>
              <Input
                id="estimatedMinutes"
                type="number"
                min="1"
                placeholder={t('ORGTEMPLATES_FORM_ESTIMATEDMINUTESPLACEHOLDER')}
                {...form.register('estimatedMinutes', {
                  setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                })}
              />
            </div>
          </CardContent>
        </Card>

        {/* AI Configuration Card */}
        <Card className="mb-6">
          <CardHeader
            className="cursor-pointer"
            onClick={() => setAiConfigExpanded(!aiConfigExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-selise-blue" />
                <CardTitle>{t('ORGTEMPLATES_EDIT_AI_TITLE')}</CardTitle>
                {form.watch('systemPrompt') && (
                  <Badge variant="secondary" className="text-xs">
                    {t('ORGTEMPLATES_EDIT_AI_CUSTOMBADGE')}
                  </Badge>
                )}
              </div>
              {aiConfigExpanded ? (
                <ChevronUp className="h-5 w-5 text-globe-grey" />
              ) : (
                <ChevronDown className="h-5 w-5 text-globe-grey" />
              )}
            </div>
            <CardDescription>{t('ORGTEMPLATES_EDIT_AI_DESCRIPTION')}</CardDescription>
          </CardHeader>
          {aiConfigExpanded && (
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="systemPrompt">{t('ORGTEMPLATES_EDIT_AI_PROMPTLABEL')}</Label>
                  {defaultPrompts[form.watch('slug')] && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const defaultPrompt = defaultPrompts[form.watch('slug')];
                        if (defaultPrompt) {
                          form.setValue('systemPrompt', defaultPrompt);
                        }
                      }}
                      className="h-7 text-xs gap-1"
                    >
                      <RotateCcw className="size-3" />
                      {t('ORGTEMPLATES_AI_LOADDEFAULT')}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-globe-grey">{t('ORGTEMPLATES_EDIT_AI_PROMPTHELP')}</p>
                <textarea
                  id="systemPrompt"
                  placeholder={t('ORGTEMPLATES_EDIT_AI_PROMPTPLACEHOLDER')}
                  className="flex min-h-64 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  {...form.register('systemPrompt')}
                />
                {form.watch('systemPrompt') && (
                  <div className="flex justify-between items-center text-xs text-globe-grey">
                    <span>
                      {t('ORGTEMPLATES_EDIT_AI_CHARACTERS', {
                        count: form.watch('systemPrompt')?.length ?? 0,
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => form.setValue('systemPrompt', null)}
                      className="h-6 text-xs text-destructive hover:text-destructive"
                    >
                      {t('ORGTEMPLATES_EDIT_AI_CLEAR')}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 sm:gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to="/admin/templates">{t('ORGTEMPLATES_ACTIONS_CANCEL')}</Link>
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('ORGTEMPLATES_NEW_ACTIONS_SUBMIT')}
          </Button>
        </div>
      </form>
    </div>
  );
}
